import React from 'react';

/**
 * One numbered step of the New order form (Forest handoff, Phase 1 "New order").
 *
 * A neutral card: hairline border, 12px radius, no shadow, and a tinted header
 * band with the step number, the title and optional helper links or controls
 * on the right. Every step uses the same treatment.
 *
 * Not `.card`: that clips its content (overflow-hidden), and the customer,
 * address and product autocompletes drop out of these cards.
 *
 * The class hooks (.form-section, .form-section-title, .form-section-dot) are
 * what Fable's overrides in appearance.css target. The number is drawn from
 * data-step by ::before, the same way Fable's override draws it, so it shows
 * once in both appearances.
 */
const OrderFormSection = ({
  step,
  title,
  aside,
  footer,
  children,
  className = '',
  bodyClassName = '',
  ...rest
}) => (
  <section className={`form-section rounded-lg border border-gray-200 bg-white ${className}`} {...rest}>
    <div className="form-section-title flex min-h-[49px] flex-wrap items-center gap-x-3 gap-y-2 rounded-t-lg border-b border-gray-200 bg-floral-50 px-5 py-3">
      <span
        className="form-section-dot grid h-6 w-6 shrink-0 place-items-center rounded-full bg-floral-700 text-xs font-bold text-white before:content-[attr(data-step)]"
        data-step={step}
        aria-hidden="true"
      />
      <h2 className="text-[15px] font-semibold text-gray-900">
        <span className="sr-only">Step {step}: </span>
        {title}
      </h2>
      {aside && <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-1">{aside}</div>}
    </div>
    <div className={`px-5 py-[18px] ${bodyClassName}`}>{children}</div>
    {footer}
  </section>
);

// Link ink as in PageHeader: floral-700 in light, floral-900 in Forest dark
// (= --f-link), written out in full for Tailwind's scanner.
const FOREST_DARK_LINK =
  '[html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900 [html[data-scheme=dark]:not([data-appearance=fable])_&]:hover:text-floral-800';

/** A text-style helper link for a section header (13px/500, link ink). */
export const SectionLink = ({ className = '', ...props }) => (
  <button
    type="button"
    className={`text-[13px] font-medium text-floral-700 hover:text-floral-800 disabled:cursor-not-allowed disabled:text-gray-400 ${FOREST_DARK_LINK} ${className}`}
    {...props}
  />
);

export default OrderFormSection;
